import isNil from 'lodash/isNil';
import { type ComponentProps, type ReactElement, type ReactNode } from 'react';

import { Popover as PopoverRoot, PopoverContent, PopoverTrigger } from '@ui/components/base/popover';

const DEFAULT_SIDE_OFFSET = 4;
const DEFAULT_HOVER_DELAY = 0;
const DEFAULT_HOVER_CLOSE_DELAY = 100;
const KEYBOARD_INTERACTION = 'keyboard';

const focusOnKeyboardInteractionOnly = (interactionType: string) => interactionType === KEYBOARD_INTERACTION;

interface PopoverBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  className?: string;
  positionerZIndex?: number;
  initialFocus?: ComponentProps<typeof PopoverContent>['initialFocus'];
  finalFocus?: ComponentProps<typeof PopoverContent>['finalFocus'];
}

interface TriggerPopoverProps extends PopoverBaseProps {
  trigger: ReactElement;
  openOnHover?: boolean;
  delay?: number;
  closeDelay?: number;
  nativeButton?: boolean;
}

interface AnchorPopoverProps extends PopoverBaseProps {
  /** An element rendered elsewhere: the popover only opens while it is in the document. */
  anchor: Element | null;
}

type PopoverProps = TriggerPopoverProps | AnchorPopoverProps;

const Popover = (props: PopoverProps) => {
  const {
    open,
    onOpenChange,
    children,
    align = 'center',
    side = 'bottom',
    sideOffset = DEFAULT_SIDE_OFFSET,
    className,
    positionerZIndex,
    initialFocus,
    finalFocus,
  } = props;
  const contentProps = { align, className, positionerZIndex, side, sideOffset };

  if ('anchor' in props) {
    const { anchor } = props;
    return (
      <PopoverRoot open={open && !isNil(anchor) && anchor.isConnected} onOpenChange={onOpenChange}>
        <PopoverContent anchor={anchor} finalFocus={finalFocus} initialFocus={initialFocus} {...contentProps}>
          {children}
        </PopoverContent>
      </PopoverRoot>
    );
  }

  const {
    trigger,
    openOnHover = false,
    delay = DEFAULT_HOVER_DELAY,
    closeDelay = DEFAULT_HOVER_CLOSE_DELAY,
    nativeButton = true,
  } = props;
  const focusProps = openOnHover
    ? {
        initialFocus: initialFocus ?? focusOnKeyboardInteractionOnly,
        finalFocus: finalFocus ?? focusOnKeyboardInteractionOnly,
      }
    : { initialFocus, finalFocus };

  return (
    <PopoverRoot open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        nativeButton={nativeButton}
        openOnHover={openOnHover}
        delay={delay}
        closeDelay={closeDelay}
        render={trigger}
      />
      <PopoverContent {...contentProps} {...focusProps}>
        {children}
      </PopoverContent>
    </PopoverRoot>
  );
};

export { Popover };
export type { AnchorPopoverProps, PopoverProps, TriggerPopoverProps };
